// src/components/Query/QueryForm.styles.ts
//
// Styling for the query form itself. These are the form's own styles, lifted
// from the old standalone /query page with three changes:
//
//   - The page-level card (QueryContainer), its 20px radius and its 2.2rem
//     heading are gone. The window supplies the frame and the title, so the
//     form only styles the form.
//   - The submit control keys off the real :disabled pseudo-class rather than a
//     disabled prop, so a disabled button is announced as disabled.
//   - Field spacing uses the theme spacing scale.
//
// The two-column row collapses at the mobile breakpoint rather than relying only
// on flex-wrap, so the pair is stacked deliberately on a phone instead of
// whatever the wrap happens to produce.
import styled, { css } from 'styled-components';

import { device } from '../../styles/breakpoints';

export const Lead = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.text};
  opacity: 0.85;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const Row = styled.div`
  display: flex;
  gap: 1.25rem;

  /* Second line of defence: even above the breakpoint, a field pair that cannot
     fit at its minimum width wraps rather than overflowing the window. */
  flex-wrap: wrap;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1.25rem;
  }
`;

export const Field = styled.div`
  flex: 1 1 200px;
  min-width: 0;

  @media ${device.mobile} {
    flex: 1 1 auto;
  }
`;

const controlBase = css`
  width: 100%;
  padding: 0.7rem 0.85rem;
  font-family: inherit;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
  }

  /* globalStyles.ts already sets an accent border and a soft ring on
     input:focus, so this only needs to keep the two consistent and to stop the
     focus state while a send is in flight. */
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.text};
`;

export const Input = styled.input`
  ${controlBase};
`;

export const TextArea = styled.textarea`
  ${controlBase};
  min-height: 130px;
  /* Vertical only: a horizontal grip would let a long line widen the window. */
  resize: vertical;
`;

export const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  /* Aligns with the inputs above rather than stretching: the form is a column,
     so a full-width submit would read as a banner. */
  align-self: flex-end;
  padding: 0.7rem 1.4rem;
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.background};
  background: ${({ theme }) => theme.primary};
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.accent};
    border-color: ${({ theme }) => theme.accent};
  }

  &:active:not(:disabled) {
    transform: none;
  }

  /* The global focus rule is more specific than a bare class, so it is
     restated here to keep the two-tone ring. */
  &&:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media ${device.mobile} {
    align-self: stretch;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

export const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #c62828;
  background-color: rgba(244, 67, 54, 0.1);
  border-left: 3px solid #f44336;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  /* role="alert" is on the element, so this is decorative emphasis only. */
  animation: error-slide-in 0.3s ease;

  @keyframes error-slide-in {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
